/**
 * What Bailey sees: the sheet the queue header's source line opens. The
 * Team's pick list (`PickList.tsx`): the tickets Bailey reads, in her order,
 * from the same read she picks from, under why these and no others, with
 * what she picked from it today. The filter is shown as written and is
 * edited in Team settings under Bailey, never here.
 */
import type { CSSProperties } from "react";
import type { TeamPickedToday } from "@yagni/shared";
import { Fold, MenuItem, RecordHead, SheetFoot } from "../queue/index.js";
import { filterTextOf, type StoredBacklog } from "./backlogFilterText.js";
import { PickListBasis, TicketLink, pickListEmptySentence, usePickList } from "./PickList.js";
import { toast } from "../ui/toast.js";
import { captureUiError } from "../sentry.js";

export interface WhatBaileySeesProps {
  deskId: string;
  teamName: string;
  attachment: StoredBacklog | null;
  /** "Jira · COREX query", as the header prints it. */
  sourceLine: string | null;
  /** When Bailey picks, e.g. "weekdays 09:00". */
  schedule: string | null;
  cap: number | null;
  pickedToday: TeamPickedToday | null;
  pickedItems: ReadonlyArray<{ id: string; identifier: string; title: string }>;
  onEditFilter: () => void;
  onOpenItem: (id: string) => void;
  onClose: () => void;
}

function nextPickWords(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" });
}

export const COPY_FAILED = "Could not copy. Select the text and copy it by hand.";

/** Copies to the clipboard; a browser that refuses says so, and it is reported. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard) throw new Error("Clipboard is not available");
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    toast.error(COPY_FAILED);
    captureUiError(err, { area: "Teams", operation: "what_bailey_sees_copy", severity: "warning", expected: true });
    return false;
  }
}

export function WhatBaileySees({
  deskId,
  teamName,
  attachment,
  sourceLine,
  schedule,
  cap,
  pickedToday,
  pickedItems,
  onEditFilter,
  onOpenItem,
  onClose,
}: WhatBaileySeesProps) {
  const filter = filterTextOf(attachment);
  const pickList = usePickList(deskId);
  const list = pickList.list;
  const picks = schedule ? `picks ${schedule}${cap != null ? ` · up to ${cap}` : ""}` : cap != null ? `up to ${cap} a day` : null;
  const pickedCount = pickedToday ? (pickedToday.cap != null ? `${pickedToday.count} of ${pickedToday.cap}` : String(pickedToday.count)) : String(pickedItems.length);

  return (
    <>
      <RecordHead
        recordKey="WHAT BAILEY SEES"
        kick={
          <span className="stagebtn" style={{ "--stage": "var(--agent-pm)" } as CSSProperties}>
            <span
              aria-hidden
              style={{ width: 8, height: 8, borderRadius: 99, background: "var(--agent-pm)", display: "inline-block" }}
            />
            Backlog
          </span>
        }
        title={teamName}
        noExpand
        onClose={onClose}
        more={
          <>
            {filter != null && <MenuItem onSelect={() => void copyText(filter)}>Copy the filter</MenuItem>}
            <MenuItem onSelect={() => void copyText(window.location.href)}>Copy link</MenuItem>
          </>
        }
        meta={
          <>
            {sourceLine && <span className="chip">{sourceLine}</span>}
            {list && (
              <span className="chip" style={{ color: "var(--accent-active)" }} data-testid="sees-match">
                {list.next.length} to pick from
              </span>
            )}
            {picks && <span className="chip">{picks}</span>}
          </>
        }
      />
      <div className="sbody" data-testid="what-bailey-sees">
        <p className="body-p" style={{ color: "var(--text-primary)" }}>
          The tickets Bailey picks from, in the order she reads them. This is her own read, not a preview: nothing
          outside it is picked. Add tickets puts one in by hand; the backlog stays as it is.
        </p>
        {list == null ? (
          <div className={pickList.failed ? "blst warn" : "blst quiet"}>
            {pickList.failed ? "Could not read what Bailey reads just now." : "Reading the backlog as Bailey does…"}
          </div>
        ) : (
          <>
            <PickListBasis list={list} />
            {list.next.length === 0 ? (
              <div className="blst warn" data-testid="pick-list-empty">
                <b>Nothing to pick from.</b> {pickListEmptySentence(list)}
              </div>
            ) : (
              <ol className="blrows" data-testid="pick-list" style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
                {list.next.map((ticket, i) => (
                  <li key={`${ticket.source}:${ticket.identifier}`} className="blrow pl" data-testid="pick-list-row">
                    <span className="n">{i + 1}</span>
                    <span className="k">{ticket.identifier}</span>
                    <span className="t" title={ticket.title}>
                      {ticket.title}
                    </span>
                    <span className="tags">
                      {ticket.priority && <span className="chip">{ticket.priority}</span>}
                      {ticket.assignee && <span className="chip">{ticket.assignee}</span>}
                    </span>
                    <TicketLink ticket={ticket} />
                  </li>
                ))}
              </ol>
            )}
            {list.next.length >= list.limit && (
              <p className="fact" style={{ margin: 0 }}>
                Bailey reads the first {list.limit} each time. Narrow the backlog to bring others into reach.
              </p>
            )}
            {list.onTeam.length > 0 && (
              <div className="span">
                <Fold title="Already on a Team" count={String(list.onTeam.length)}>
                  {list.onTeam.map((ticket) => (
                    <p key={`${ticket.source}:${ticket.identifier}`} style={{ margin: "2px 0" }}>
                      <b>{ticket.identifier}</b> {ticket.title}
                      <span className="fact"> · {ticket.deskName ?? "a Team"}, {ticket.phase}</span>
                    </p>
                  ))}
                </Fold>
              </div>
            )}
          </>
        )}
        <div className="span">
          <Fold title="Picked from it today" count={pickedCount}>
            {pickedItems.length === 0 ? (
              <p>Nothing yet.</p>
            ) : (
              <p style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {pickedItems.map((item) => (
                  <button key={item.id} type="button" className="ref" title={item.title} onClick={() => onOpenItem(item.id)}>
                    {item.identifier}
                  </button>
                ))}
              </p>
            )}
            {pickedToday?.nextPickAt && <p>Next pick {nextPickWords(pickedToday.nextPickAt)}.</p>}
          </Fold>
        </div>
      </div>
      <SheetFoot
        why={
          <>
            Changing the backlog changes the next pick, <b>not the tickets already in the queue.</b>
          </>
        }
        secondary={{ label: "Check now", onClick: pickList.recheck, disabled: pickList.loading }}
        primary={{ label: "Edit the filter", onClick: onEditFilter }}
      />
    </>
  );
}
